/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { type SearchResultDesignPartial } from '@atlassian/dynamic-cards-search-client';

import { LastModified } from '../../../../../../../common/ui/last-modified';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	getIntegrationKey,
	isAWSThumbnailUrlExpired,
	isNonPublic,
} from '../../../../../../../common/utils/search-result-utils';
import {
	useBootstrap,
	usePrimaryProduct,
	useSelectedProduct,
} from '../../../../../../../controllers/store';

import { messages } from './messages';

const thumbnailContainerStyles = xcss({
	gridArea: 'body',
});
const thumbnailImageStyles = css({
	objectFit: 'cover',
	borderRadius: token('radius.medium'),
	height: '108px',
	width: '224px',
	borderWidth: token('border.width.selected'),
	borderStyle: 'solid',
	borderColor: token('color.text.disabled'),
});

const descriptionLinkStyles = xcss({
	display: 'inline-block',
});

interface DesignSearchResultProps {
	icon: React.ReactNode;
	partial: SearchResultDesignPartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

export const DesignSearchResult = ({
	partial,
	position,
	icon,
}: DesignSearchResultProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();
	const integrationKey = getIntegrationKey(partial.integrationId);

	const { lastModifiedDate, url, permissionLevel, entity, title } = partial;

	const thumbnailUrl = entity?.thumbnail?.externalUrl;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;
	const label = formatMessage(messages.designLabel);

	const body =
		!thumbnailUrl || isAWSThumbnailUrlExpired(thumbnailUrl) ? null : (
			<Anchor xcss={[descriptionLinkStyles]} rel={'noreferrer'} target={'_blank'} href={url}>
				<Box testId="thumbnail-container" xcss={thumbnailContainerStyles}>
					<img css={thumbnailImageStyles} src={thumbnailUrl} alt="" />
				</Box>
			</Anchor>
		);

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	return (
		<SearchResult
			id={partial.id}
			icon={icon}
			description={body}
			headerMetadata={
				<SearchMetadata
					product={integrationKey}
					productSelected={!!selectedProduct}
					parts={[label, ['last-modified', lastModifiedComponent], ['padlock', padlockIcon]]}
				/>
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
		/>
	);
};
